import {
	getOrderingEarnedPoints,
	getOrderingMaxPoints,
	resolveCorrectOrder,
} from "@memorang/helpers/ordering";
import { resolveMaxScore } from "@memorang/helpers/scoring/max-score";
import type {
	BaseItemSchema,
	Grading,
	OriginalItemTypes,
} from "@memorang/types";
import useSessionStore from "../hooks/useSessionStore";
import { matchDictationWords } from "./dictation";
import { highlightWordChoices } from "./original-question";
import {
	type BlankVerdict,
	getMappingVerdicts,
	getTextVerdicts,
} from "./points";
import { resolveHighlightedWords } from "./pte-feedback-response";
import {
	getCorrectAnswerIdsFromAnswerKey,
	getHighlightIncorrectWordsEarnedPoints,
} from "./session";

export type ClientGradingUnit = {
	id: string;
	correct: boolean;
	received?: string;
};

export type ClientGrading = {
	version: 1;
	itemType: string;
	units: ClientGradingUnit[];
	extraSelections?: string[];
	earnedPoints: number;
	maxPoints: number;
};

const isStringRecord = (value: unknown): value is Record<string, string> =>
	value != null &&
	typeof value === "object" &&
	!Array.isArray(value) &&
	Object.values(value).every((entry) => typeof entry === "string");

const isStringArray = (value: unknown): value is string[] =>
	Array.isArray(value) && value.every((entry) => typeof entry === "string");

const toUnit = (
	id: string,
	correct: boolean,
	received?: string,
): ClientGradingUnit =>
	received === undefined ? { id, correct } : { id, correct, received };

type BlankAnswerKey = Extract<
	Grading,
	{ type: "ANSWER_KEY_TEXT" | "ANSWER_KEY_MAPPING" }
>["answerKey"];

const allHaveBlankIds = (answerKey: BlankAnswerKey): boolean =>
	answerKey.every((entry) => "blankId" in entry);

const gradeBlanks = (
	itemType: OriginalItemTypes,
	verdicts: BlankVerdict[],
): ClientGrading => ({
	version: 1,
	itemType,
	units: verdicts.map(({ id, correct }) => toUnit(id, correct)),
	earnedPoints: verdicts.reduce(
		(acc, verdict) => acc + (verdict.correct ? verdict.points : 0),
		0,
	),
	maxPoints: verdicts.reduce((acc, verdict) => acc + verdict.points, 0),
});

const gradeHighlightedWords = (
	item: BaseItemSchema,
	itemType: OriginalItemTypes,
	scoring: Extract<Grading, { type: "ANSWER_KEY_SELECTION" }>,
): ClientGrading | undefined => {
	const itemId = item.id;
	if (itemId == null || item.response?.type !== "INLINE_TEXT_SELECTION") {
		return undefined;
	}
	const selectedChoiceIds =
		useSessionStore.getState().itemStates?.get(itemId)?.selectedChoiceIds ??
		item.answer?.selectedChoiceIds;
	if (!selectedChoiceIds) {
		return undefined;
	}
	const choices = highlightWordChoices(item.response.incorrectWords);
	const correctIds = getCorrectAnswerIdsFromAnswerKey(choices, scoring);
	const maxPoints = resolveMaxScore(scoring);
	if (correctIds.length === 0 || maxPoints == null) {
		return undefined;
	}
	const selectedSet = new Set(selectedChoiceIds);
	const correctSet = new Set(correctIds);
	return {
		version: 1,
		itemType,
		units: correctIds.map((id) => toUnit(id, selectedSet.has(id))),
		extraSelections: resolveHighlightedWords(
			item,
			selectedChoiceIds.filter((id) => !correctSet.has(id)),
		),
		earnedPoints: getHighlightIncorrectWordsEarnedPoints(
			choices,
			scoring,
			selectedChoiceIds,
		),
		maxPoints,
	};
};

const gradeDictation = (
	itemType: OriginalItemTypes,
	scoring: Extract<Grading, { type: "ANSWER_KEY_TEXT" }>,
	responseText: string,
): ClientGrading => {
	const answerKeyWords = scoring.answerKey.map((entry) => entry.word);
	const { matchedTokens, unmatchedTokens } = matchDictationWords(
		answerKeyWords,
		responseText,
	);
	return {
		version: 1,
		itemType,
		units: matchedTokens.map((token, index) =>
			toUnit(String(index), token !== null, token ?? undefined),
		),
		extraSelections: unmatchedTokens,
		earnedPoints: matchedTokens.filter((token) => token !== null).length,
		maxPoints: answerKeyWords.length,
	};
};

const gradeOrdering = (
	itemType: OriginalItemTypes,
	scoring: Extract<Grading, { type: "ANSWER_KEY_ORDERING" }>,
	selectedOrder: string[],
): ClientGrading | undefined => {
	const correctOrder = resolveCorrectOrder(
		scoring.answerKey,
		scoring.correctOrder,
	);
	if (!correctOrder) {
		return undefined;
	}
	return {
		version: 1,
		itemType,
		units: correctOrder.map((expectedId, index) =>
			toUnit(
				expectedId,
				selectedOrder[index] === expectedId,
				selectedOrder[index],
			),
		),
		earnedPoints: getOrderingEarnedPoints(
			scoring.answerKey,
			selectedOrder,
			scoring.correctOrder,
		),
		maxPoints: getOrderingMaxPoints(scoring.answerKey),
	};
};

const gradeByItemType = (
	item: BaseItemSchema,
	itemType: OriginalItemTypes,
	scoring: Grading,
	resolvedResponse: unknown,
): ClientGrading | undefined => {
	switch (itemType) {
		case "FIBTIL":
		case "FIBTIL_CORE":
			return scoring.type === "ANSWER_KEY_TEXT" &&
				isStringRecord(resolvedResponse) &&
				allHaveBlankIds(scoring.answerKey)
				? gradeBlanks(
						itemType,
						getTextVerdicts(scoring.answerKey, resolvedResponse),
					)
				: undefined;
		case "FIBDDR":
		case "FIBDDR_CORE":
		case "FIBDPR":
		case "FIBDPR_CORE":
			return scoring.type === "ANSWER_KEY_MAPPING" &&
				isStringRecord(resolvedResponse) &&
				allHaveBlankIds(scoring.answerKey)
				? gradeBlanks(
						itemType,
						getMappingVerdicts(scoring.answerKey, resolvedResponse),
					)
				: undefined;
		case "HIWL":
		case "HIWL_CORE":
			return scoring.type === "ANSWER_KEY_SELECTION"
				? gradeHighlightedWords(item, itemType, scoring)
				: undefined;
		case "WFDL":
		case "WFDL_CORE":
			return scoring.type === "ANSWER_KEY_TEXT" &&
				typeof resolvedResponse === "string"
				? gradeDictation(itemType, scoring, resolvedResponse)
				: undefined;
		case "ROPR":
		case "ROPR_CORE":
			return scoring.type === "ANSWER_KEY_ORDERING" &&
				isStringArray(resolvedResponse)
				? gradeOrdering(itemType, scoring, resolvedResponse)
				: undefined;
		default:
			return undefined;
	}
};

export const computeClientGrading = (
	item: BaseItemSchema,
	resolvedResponse: unknown,
): ClientGrading | undefined => {
	try {
		const itemType = item.metadata?.type;
		const scoring = item.scoring;
		if (!(itemType && scoring?.type)) {
			return undefined;
		}
		return gradeByItemType(item, itemType, scoring, resolvedResponse);
	} catch {
		return undefined;
	}
};
